import React, { RefObject, useState } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { Common } from "lib/types"
import { FrontendOutcome, Outcomes } from "lib/types/discount"
import {
  InputDiscountValueType,
  InputOutcome,
  InputSubOutcome,
  TagCondition
} from "lib/types/generated/graphql-types"
import alertActions from "lib/store/services/Alert/AlertSlice"
import { useAppDispatch } from "lib/store"
import { TagConditions } from "components/Discount/Ui/TagConditions/TagConditions"
import { SubOutcome } from "../../SubOutcome"
import { WithAuth } from "../../../Auth"
import { PermissionGroups } from "../../../../routes/constants"
import FallbackCheckbox from "../../../Ui/Form/FallbackCheckbox"
import SecondaryButton from "../../../Ui/Buttons/SecondaryButton"
import {ReactComponent as TrashIcon} from "images/icons/trash-can.svg"
import {ReactComponent as PlusIcon} from "images/icons/circle-plus.svg"
import {
  ButtonWrapper,
  Description,
  Divider,
  Label,
  StyledCheckbox,
  SubOutcomeHeader,
  SubOutcomes,
  SubOutcomeTitle
} from "./StackedProducts.styled"

type Props = {
  outcome?: FrontendOutcome
  addOutcome: (data: Outcomes) => void
  currencyOptions: Common.Option[]
  submitRef: RefObject<HTMLButtonElement | null>
}

export const StackedProducts = ({ outcome, addOutcome, currencyOptions, submitRef }: Props) => {
  const dispatch = useAppDispatch()

  const valueTypes = (
    outcome?.data?.subOutcomes?.map((subOutcome) => subOutcome?.discountValue?.type) || []
  ).filter((type): type is string => type !== undefined)

  const [subOutcomesCount, setSubOutcomesCount] = useState<number>(
    outcome?.data?.subOutcomes?.length ?? 1
  )

  const [tagConditions, setTagConditions] = useState<TagCondition[]>(
    outcome?.data?.discountItemRule?.tagConditions ?? []
  )
  const [excludeDiscountedPrices, setExcludeDiscountedPrices] = useState(
    outcome?.data?.discountItemRule?.excludeDiscountedPrices ?? false
  )
  const [excludeFullPrices, setExcludeFullPrices] = useState(
    outcome?.data?.discountItemRule?.excludeFullPrices ?? false
  )

  const {
    handleSubmit,
    setValue,
    control,
    register,
    formState: { errors },
    resetField,
    unregister
  } = useForm<InputOutcome>()

  const addSubOutcome = () => {
    setSubOutcomesCount((prev) => prev + 1)
  }

  const removeSubOutcome = () => {
    if (subOutcomesCount > 1) {
      const lastIndex = subOutcomesCount - 1
      unregister(`subOutcomes.${lastIndex}`)
      setSubOutcomesCount((prev) => prev - 1)
    }
  }

  const onSubmit: SubmitHandler<InputOutcome> = (data) => {
    if (data?.subOutcomes?.some((subOutcome) => !subOutcome?.discountValue?.type)) {
      dispatch(
        alertActions.actions.createAlert({
          message: "Please select a discount value type",
          type: "error"
        })
      )
      return
    }

    if (
      valueTypes.includes("FIXED") &&
      data.subOutcomes?.some(
        (subOutcome) =>
          subOutcome?.discountValue?.type === InputDiscountValueType.Fixed &&
          subOutcome?.discountValue?.fixedDiscounts?.length === 0
      )
    ) {
      dispatch(
        alertActions.actions.createAlert({
          message: "At least one threshold is required",
          type: "error"
        })
      )
      return
    }

    addOutcome({
      subOutcomes: data.subOutcomes as InputSubOutcome[],
      combineItemDiscount: data.combineItemDiscount ?? false,
      discountItemRule: {
        tagConditions: tagConditions,
        excludeDiscountedPrices: excludeDiscountedPrices,
        excludeFullPrices: excludeFullPrices
      }
    })
  }

  return (
    <>
      <Divider />
      <form>
        <Label>{"Sub outcomes"}</Label>
        <Description>{"Add tiered discounts based on quantity triggers."}</Description>

        <SubOutcomes>
          {Array.from({ length: subOutcomesCount }).map((_, index) => {
            return (
              <div key={index}>
                <SubOutcomeHeader>
                  <SubOutcomeTitle>Sub outcome {index + 1}</SubOutcomeTitle>
                </SubOutcomeHeader>
                <SubOutcome
                  index={index}
                  outcome={outcome}
                  currencyOptions={currencyOptions}
                  control={control}
                  errors={errors}
                  setValue={setValue}
                  register={register}
                  resetField={resetField}
                  hideDiscountQuantity={true}
                />
              </div>
            )
          })}
        </SubOutcomes>

        <ButtonWrapper>
          <SecondaryButton handleClick={addSubOutcome} type="button">
            <PlusIcon/>
            Add sub outcome
          </SecondaryButton>
          {subOutcomesCount > 1 && <SecondaryButton
            handleClick={removeSubOutcome}
            type="button"
          >
            <TrashIcon/>
            Remove
          </SecondaryButton>}

        </ButtonWrapper>

        <WithAuth
          requiredGroups={PermissionGroups.discounts.write}
          fallback={
            <FallbackCheckbox
              label={"Combine item discount"}
              description={"Combine discount with other discounts"}
              value={outcome?.data?.combineItemDiscount ?? false}
            />
          }
        >
          <StyledCheckbox
            control={control}
            name="combineItemDiscount"
            label="Combine item discount"
            description="Combine discount with other discounts"
            defaultValue={outcome?.data?.combineItemDiscount ?? false}
          />
        </WithAuth>

        <button
          onClick={handleSubmit(onSubmit)}
          ref={submitRef}
          type="submit"
          style={{ display: "none" }}
        />
      </form>
      <Divider />
      <TagConditions
        tagConditions={tagConditions}
        setTagConditions={setTagConditions}
        excludeDiscountedPrices={excludeDiscountedPrices}
        setExcludeDiscountedPrices={setExcludeDiscountedPrices}
        excludeFullPrices={excludeFullPrices}
        setExcludeFullPrices={setExcludeFullPrices}
      />
    </>
  )
}
